import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { downloadInvoicePdf } from '@/lib/invoicePdf';
import type { FinanceInvoice, FinanceInvoiceItem } from '@/lib/finance';

interface InvoicePdfButtonProps {
  /** Identifiant du document créé par l'assistant. */
  invoiceId: number;
  /** Numéro du document (facture ou devis), pour le libellé. */
  number: string;
  /** Type de document créé. */
  docType?: 'invoice' | 'quote';
}

/**
 * Bouton « Télécharger en PDF » affiché directement dans la réponse de
 * l'assistant après la création d'une facture ou d'un devis.
 *
 * Le document est relu depuis la base (Finance) au moment du clic, avec les
 * lignes réelles et le profil de l'entreprise : le PDF est strictement
 * identique à celui exporté depuis Finance → Factures / Devis.
 */
export default function InvoicePdfButton({ invoiceId, number, docType = 'invoice' }: InvoicePdfButtonProps) {
  const company = useCompanyProfile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isQuote = docType === 'quote';

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data: invoice, error: invErr } = await supabase
        .from('finance_invoices')
        .select('*')
        .eq('id', invoiceId)
        .maybeSingle();
      if (invErr) throw invErr;
      if (!invoice) throw new Error('Document introuvable');

      const { data: items, error: itemsErr } = await supabase
        .from('finance_invoice_items')
        .select('*')
        .eq('invoice_id', invoiceId);
      if (itemsErr) throw itemsErr;

      downloadInvoicePdf(
        invoice as FinanceInvoice,
        (items || []) as FinanceInvoiceItem[],
        company.profile,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Téléchargement impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="not-prose">
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        title={`Télécharger ${isQuote ? 'le devis' : 'la facture'} ${number} en PDF`}
      >
        <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-2-line'}></i>
        {busy
          ? 'Préparation…'
          : `Télécharger ${isQuote ? 'le devis' : 'la facture'} en PDF`}
      </button>
      {error && (
        <p className="mt-1 text-[11px] text-red-600 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}
    </div>
  );
}